import { useState, useEffect, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/16/solid'
import { useDebounce } from '@/utils/fetch'
import apiFetch from '@/services/apiService'
import SuggestionsPortal from '@/components/SuggestionsPortal'
import { Badge } from '@/components/ui'

import { formatBienLabel, getBienSuggestionLabel } from './biensSlice'

export default ({type = "", objectField, originalData = null, 
    label = "Bien",
    placeholder = "Rechercher un bien",
    validator = {},
    disabled = false
  }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(null)
  const inputRef = useRef(null)

  const name = objectField.name

  // Récupérer les suggestions de contacts
  const fetchSuggestions = useDebounce(async (term) => {
    if (!term || term.length < 2) {
      setSuggestions([])
      return
    }
    
    setLoading(true)
    try {
      let query = { term }
      if(type) { 
        query = {...query, type: type }
      }
      
      const response = await apiFetch()
        .url(`biens/search`)
        .query(query)
        .get()
      
      setSuggestions(response.data || [])
    } catch (error) {
      console.error('Erreur lors de la recherche de biens:', error)
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }, 300)

  useEffect( () => {
    if(!objectField.value) {
      setSelected(null)
      setSearchTerm('')
    }
  }, [objectField])

  // Charger les détails du contact sélectionné
  useEffect(() => {
    if(originalData) {
      setSelected(originalData)
      setSearchTerm(getBienSuggestionLabel(originalData))
    }
  }, [originalData])

  // Gérer le changement dans le champ de recherche
  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    fetchSuggestions(value)
    setShowSuggestions(true)
    
    // Si le champ est vidé, réinitialiser le contact sélectionné
    if (!value) {
      objectField.onChange('')
      setSelected(null)
    }
  }

  // Sélectionner un contact dans les suggestions
  const handleSelect = (object) => {
    objectField.onChange(object)
    setSearchTerm(getBienSuggestionLabel(object))
    setSelected(object)
    setShowSuggestions(false)
  } 

  // Réinitialiser le contact sélectionné
  const resetSelected = () => {
    objectField.onChange('')
    setSelected(null)
    setSearchTerm('')
  }

  return (
    <div className="relative suggestions-container">
      <label className={`input w-full ${validator.hasError ? 'validator' : ''}`}
        {...(validator.hasError && validator.hasError(name) ? {'aria-invalid': true} : {})}
      >
        <span className="label">{label}</span>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full"
          ref={inputRef}
        />
        {loading && <span className="loading loading-spinner loading-sm text-base-content/70 absolute right-2 top-1/2 transform -translate-y-1/2"></span>}
        {!loading && (selected || searchTerm.length > 0) && (
          <button
          type="button"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-base-content/70 rounded-full w-4 h-4 transition-colors duration-200 ease-in hover:bg-gray-200 focus:outline-none focus:bg-gray-300 dark:text-white dark:hover:bg-dark-500 dark:focus:bg-dark-450 hover:bg-gray-200"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              resetSelected()
            }}
          >
            <XMarkIcon/>
        </button>
        )}
      </label>
      {validator.getErrorMessage && (
        <div className="validator-hint hidden empty:py-[0.6rem]"
          style={{marginTop: '0.5em'}}
        >
          {validator.getErrorMessage(name)}
        </div>
      )}
      

      <SuggestionsPortal
        show={showSuggestions && (suggestions.length > 0 || (searchTerm.length >= 2 && !loading))}
        anchorRef={inputRef}
      >
      {suggestions.length > 0 ? (
        <div className="absolute z-10 w-full bg-base-100 shadow-lg rounded-lg mt-1 border border-base-300 max-h-60 overflow-y-auto">
          {suggestions.map(item => (
            <div 
              key={item.id}
              className="p-2 hover:bg-base-200 cursor-pointer"
              onClick={() => handleSelect(item)}
            >
              <div>
                <Badge text={item.type_bien}/>
                <span className="ml-2">{getBienSuggestionLabel(item)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="absolute z-10 w-full bg-base-100 shadow-lg rounded-lg mt-1 border border-base-300 p-2 space-y-2">
          <p>Aucun bien trouvé</p>
          <p className="text-sm">
            Veuillez d'abord <a href="/biens" className="text-primary hover:underline">créer le bien</a> dans la liste des biens.
          </p>
        </div>
      )}
      </SuggestionsPortal> 
    </div>
  )
}